import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield,
  Wifi,
  Ticket,
  UserCog,
  CreditCard,
  Wrench,
  CheckCircle2,
  XCircle,
  Loader2,
  Clock,
  Zap,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import type { AgentNode as AgentNodeType } from '../../types';

interface AgentNodeProps {
  node: AgentNodeType;
  isActive?: boolean;
  onClick?: () => void;
  delay?: number;
}

const agentIconMap = {
  auth: Shield,
  network: Wifi,
  ticket: Ticket,
  supervisor: UserCog,
  billing: CreditCard,
  tech: Wrench,
};

const statusConfig = {
  idle: {
    border: 'border-white/10',
    bg: 'bg-white/5',
    glow: '',
    iconColor: 'text-text-muted',
  },
  running: {
    border: 'border-vokentra/50',
    bg: 'bg-vokentra/10',
    glow: 'shadow-glow-red',
    iconColor: 'text-vokentra',
  },
  completed: {
    border: 'border-success/50',
    bg: 'bg-success/10',
    glow: 'shadow-glow-green',
    iconColor: 'text-success',
  },
  failed: {
    border: 'border-error/50',
    bg: 'bg-error/10',
    glow: 'shadow-[0_0_15px_rgba(255,51,102,0.3)]',
    iconColor: 'text-error',
  },
  waiting: {
    border: 'border-warning/50',
    bg: 'bg-warning/10',
    glow: '',
    iconColor: 'text-warning',
  },
};

export function AgentNodeComponent({ node, isActive = false, onClick, delay = 0 }: AgentNodeProps) {
  const [showTooltip, setShowTooltip] = useState(false);
  const Icon = agentIconMap[node.type] || Wrench;
  const config = statusConfig[node.status];

  const formatLatency = (ms: number): string => {
    if (ms < 1000) return `${Math.round(ms)}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  return (
    <motion.div
      className="relative"
      initial={{ opacity: 0, scale: 0, filter: 'blur(10px)' }}
      animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
      transition={{ delay, duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <motion.button
        onClick={onClick}
        className={cn(
          'relative flex flex-col items-center gap-2 p-4 rounded-xl border transition-colors',
          'backdrop-blur-sm min-w-[100px]',
          config.border,
          config.bg,
          config.glow,
          isActive && 'ring-2 ring-vokentra/50',
          onClick && 'cursor-pointer hover:bg-white/10'
        )}
        whileHover={{ scale: 1.05, y: -2 }}
        whileTap={{ scale: 0.98 }}
        animate={
          node.status === 'running'
            ? {
                boxShadow: [
                  '0 0 5px rgba(230, 0, 0, 0.2)',
                  '0 0 20px rgba(230, 0, 0, 0.4)',
                  '0 0 5px rgba(230, 0, 0, 0.2)',
                ],
              }
            : node.status === 'failed'
            ? {
                x: [0, -3, 3, -3, 3, 0],
                transition: { duration: 0.5 },
              }
            : undefined
        }
        transition={
          node.status === 'running'
            ? { duration: 2, repeat: Infinity, ease: 'easeInOut' }
            : undefined
        }
      >
        {/* Status indicator */}
        <div className="absolute -top-1.5 -right-1.5">
          {node.status === 'completed' && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 500, damping: 25 }}
            >
              <CheckCircle2 className="w-5 h-5 text-success bg-background rounded-full" />
            </motion.div>
          )}
          {node.status === 'failed' && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 500, damping: 25 }}
            >
              <XCircle className="w-5 h-5 text-error bg-background rounded-full" />
            </motion.div>
          )}
          {node.status === 'running' && (
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
            >
              <Loader2 className="w-5 h-5 text-vokentra bg-background rounded-full" />
            </motion.div>
          )}
          {node.status === 'waiting' && (
            <Clock className="w-5 h-5 text-warning bg-background rounded-full" />
          )}
        </div>

        {/* Icon */}
        <motion.div
          className={cn(
            'w-10 h-10 rounded-lg flex items-center justify-center',
            config.bg,
            config.iconColor
          )}
          animate={
            node.status === 'running'
              ? { scale: [1, 1.1, 1] }
              : undefined
          }
          transition={
            node.status === 'running'
              ? { duration: 1.5, repeat: Infinity, ease: 'easeInOut' }
              : undefined
          }
        >
          <Icon className="w-5 h-5" />
        </motion.div>

        {/* Name */}
        <span className="text-xs font-medium text-text-primary whitespace-nowrap">
          {node.name}
        </span>

        {/* Latency badge */}
        {node.latency > 0 && (
          <motion.div
            className={cn(
              'flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-mono',
              node.latency < 500
                ? 'bg-success/20 text-success'
                : node.latency < 2000
                ? 'bg-warning/20 text-warning'
                : 'bg-error/20 text-error'
            )}
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: delay + 0.3 }}
          >
            <Zap className="w-2.5 h-2.5" />
            {formatLatency(node.latency)}
          </motion.div>
        )}
      </motion.button>

      {/* Tooltip */}
      <AnimatePresence>
        {showTooltip && (
          <motion.div
            className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50"
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            <div className="glass-panel p-3 rounded-xl min-w-[180px]">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-text-primary">
                  {node.name}
                </span>
                <span
                  className={cn(
                    'text-[10px] px-1.5 py-0.5 rounded-full font-medium',
                    node.status === 'completed' && 'bg-success/20 text-success',
                    node.status === 'failed' && 'bg-error/20 text-error',
                    node.status === 'running' && 'bg-vokentra/20 text-vokentra',
                    node.status === 'idle' && 'bg-white/10 text-text-muted',
                    node.status === 'waiting' && 'bg-warning/20 text-warning'
                  )}
                >
                  {node.status}
                </span>
              </div>

              {node.latency > 0 && (
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-text-muted">Latency</span>
                  <span className="text-text-primary font-mono">
                    {formatLatency(node.latency)}
                  </span>
                </div>
              )}

              {node.startTime && (
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-text-muted">Started</span>
                  <span className="text-text-primary">
                    {new Date(node.startTime).toLocaleTimeString()}
                  </span>
                </div>
              )}

              {node.error && (
                <div className="mt-2 p-2 bg-error/10 rounded-lg text-xs text-error">
                  {node.error}
                </div>
              )}

              {node.output && Object.keys(node.output).length > 0 && (
                <div className="mt-2">
                  <span className="text-xs text-text-muted">Output:</span>
                  <pre className="mt-1 p-2 bg-black/30 rounded-lg text-[10px] text-text-secondary overflow-auto max-h-[100px]">
                    {JSON.stringify(node.output, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
